/*
 * The buy bar at the bottom of the product page
 * (snippets/standard-buy-bar.liquid). Shared asset of the Liquid themes; the
 * source is the Cortex base theme (modules/cortex-theme), copied with
 * tools/sync-base.mjs or modules/storefront/liquid/sync.mjs.
 *
 * Three jobs, none of them theme-specific:
 * 1. Show the bar whenever the main buy button is off screen (above or
 *    below), hide it as soon as the button is back in view, and hide it
 *    while the footer is in view, so the end of the page is never covered
 *    (Thomas 09-10: on a pc the bar lay over the bottom of the page).
 * 2. Keep the bar's button a twin of the main button: same text whenever the
 *    theme changes it (a chosen size, a set total, sold out), and a tap on
 *    the bar's button clicks the main button, so every theme rule (size
 *    required, set logic, cart drawer) runs exactly once, in one place. A
 *    disabled main button turns the tap into a scroll to the form.
 * 3. Lift the bar above Shopify's preview bar when a draft theme is
 *    previewed, so the preview shows the bar where live will show it.
 */
(function () {
  const bar = document.querySelector('[data-standard-buy-bar]');
  if (!bar) return;
  const button = document.querySelector(bar.dataset.button);
  const ownButton = bar.querySelector('[data-buy-bar-button]');
  if (!button || !ownButton) return;
  // The text lives in its own span: the button also holds the picture and the price.
  const ownLabel = ownButton.querySelector('[data-buy-bar-label]') || ownButton;
  // Two price slots: under the name (desktop) and in the button (phone).
  const ownPrices = [...bar.querySelectorAll('[data-buy-bar-price]')];
  const form = button.form || button.closest('form');
  const priceSource = bar.dataset.price ? document.querySelector(bar.dataset.price) : null;

  function mirror() {
    const text = button.textContent.replace(/\s+/g, ' ').trim();
    if (text) ownLabel.textContent = text;
    if (priceSource && ownPrices.length) {
      const price = priceSource.innerText.replace(/\s+/g, ' ').trim();
      ownPrices.forEach((slot) => {
        slot.textContent = price;
        slot.hidden = price === '';
      });
    }
  }

  const watch = { childList: true, characterData: true, subtree: true };
  new MutationObserver(mirror).observe(button, { ...watch, attributes: true, attributeFilter: ['disabled'] });
  if (priceSource) new MutationObserver(mirror).observe(priceSource, watch);
  mirror();

  ownButton.addEventListener('click', () => {
    if (button.disabled) {
      const target = (bar.dataset.anchor && document.getElementById(bar.dataset.anchor)) || form || button;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    button.click();
  });

  function show(shown) {
    bar.toggleAttribute('data-shown', shown);
    bar.setAttribute('aria-hidden', shown ? 'false' : 'true');
    ownButton.tabIndex = shown ? 0 : -1;
  }

  // The footer: every section of the footer group (Online Store 2.0), else
  // the classic footer section or the page's contentinfo. Without any, a
  // marker after the last content stands for the end of the page. Each one
  // is watched, because the first section of the group can be empty.
  function footerParts() {
    const selectors = ['.shopify-section-group-footer-group', '#shopify-section-footer', 'body > footer, [role="contentinfo"]'];
    for (const selector of selectors) {
      const parts = [...document.querySelectorAll(selector)];
      if (parts.length) return parts;
    }
    const end = document.createElement('div');
    end.setAttribute('aria-hidden', 'true');
    document.body.append(end);
    return [end];
  }

  if ('IntersectionObserver' in window) {
    // One observer for the button and the footer, so both answers meet in
    // a single callback: shown only when neither is in view.
    let buttonInView = false;
    const footerInView = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === button) buttonInView = entry.isIntersecting;
        else if (entry.isIntersecting) footerInView.add(entry.target);
        else footerInView.delete(entry.target);
      });
      show(!buttonInView && footerInView.size === 0);
    });
    [button, ...footerParts()].forEach((target) => observer.observe(target));
  } else {
    show(true);
  }

  // Shopify's preview bar (draft theme in the admin) sits over the bottom of
  // the page. Its height is measured, never assumed; without it the offset is 0.
  function lift() {
    const preview = document.querySelector('#PBarNextFrameWrapper, #preview-bar-iframe');
    const height = preview ? Math.round(preview.getBoundingClientRect().height) : 0;
    bar.style.setProperty('--std-bar-offset', height + 'px');
  }
  lift();
  [1000, 3000].forEach((delay) => setTimeout(lift, delay));
  window.addEventListener('resize', lift);
})();
